/**
 * SQLFlow Deploy - License Generation and Machine Management
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Input, Space, message, Typography } from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, LinkOutlined } from '@ant-design/icons';
import type { Machine, MachineInput } from './types';
import { machineApi } from './api';
import MachineTable from './components/MachineTable';
import MachineFormModal from './components/MachineFormModal';
import EmailPreview from './components/EmailPreview';
import GenerateLinkModal from './components/GenerateLinkModal';
import SelfServiceLinkTable from './components/SelfServiceLinkTable';

const { Title } = Typography;
const { Search } = Input;

const SQLFlowDeployPage: React.FC = () => {
  // State
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [editingMachine, setEditingMachine] = useState<Machine | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Email preview state
  const [emailPreviewVisible, setEmailPreviewVisible] = useState(false);
  const [emailPreviewMachine, setEmailPreviewMachine] = useState<Machine | null>(null);

  // Generate link modal state
  const [generateLinkVisible, setGenerateLinkVisible] = useState(false);
  const [linkTableKey, setLinkTableKey] = useState(0);

  // Fetch machines
  const fetchMachines = useCallback(async (search?: string) => {
    setLoading(true);
    try {
      const response = await machineApi.list(search);
      setMachines(response.results || []);
    } catch (error) {
      message.error('Failed to load machines');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMachines();
  }, [fetchMachines]);

  // Search handler
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    fetchMachines(value);
  };

  // Open add modal
  const handleAdd = () => {
    setEditingMachine(null);
    setFormModalVisible(true);
  };

  // Open edit modal
  const handleEdit = (machine: Machine) => {
    setEditingMachine(machine);
    setFormModalVisible(true);
  };

  // Handle form submit (create/update)
  const handleFormSubmit = async (data: MachineInput) => {
    setFormLoading(true);
    try {
      if (editingMachine) {
        await machineApi.update(editingMachine.id, data);
        message.success('Machine updated successfully');
      } else {
        await machineApi.create(data);
        message.success('Machine created successfully');
      }
      setFormModalVisible(false);
      setEditingMachine(null);
      fetchMachines(searchQuery);
    } catch (error: any) {
      message.error(error.message || 'Operation failed');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    try {
      await machineApi.delete(id);
      message.success('Machine deleted successfully');
      fetchMachines(searchQuery);
    } catch (error) {
      message.error('Failed to delete machine');
    }
  };

  // Open email preview
  const handleEmailPreview = (machine: Machine) => {
    setEmailPreviewMachine(machine);
    setEmailPreviewVisible(true);
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>SQLFlow Deploy</Title>
          </Space>
        }
        extra={
          <Space>
            <Search
              placeholder="Search by customer name or email"
              allowClear
              onSearch={handleSearch}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchMachines(searchQuery)}
            >
              Refresh
            </Button>
            <Button
              icon={<LinkOutlined />}
              onClick={() => setGenerateLinkVisible(true)}
            >
              Generate Link
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Add Machine
            </Button>
          </Space>
        }
      >
        <MachineTable
          machines={machines}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onEmailPreview={handleEmailPreview}
          onRefresh={() => fetchMachines(searchQuery)}
        />
      </Card>

      {/* Self-Service Links */}
      <Card style={{ marginTop: 24 }}>
        <SelfServiceLinkTable key={linkTableKey} />
      </Card>

      {/* Add/Edit Modal */}
      <MachineFormModal
        visible={formModalVisible}
        machine={editingMachine}
        onCancel={() => {
          setFormModalVisible(false);
          setEditingMachine(null);
        }}
        onSubmit={handleFormSubmit}
        loading={formLoading}
      />

      {/* Email Preview Modal */}
      <EmailPreview
        visible={emailPreviewVisible}
        machine={emailPreviewMachine}
        onCancel={() => {
          setEmailPreviewVisible(false);
          setEmailPreviewMachine(null);
        }}
      />

      {/* Generate Link Modal */}
      <GenerateLinkModal
        visible={generateLinkVisible}
        onCancel={() => setGenerateLinkVisible(false)}
        onCreated={() => setLinkTableKey(k => k + 1)}
      />
    </div>
  );
};

export default SQLFlowDeployPage;
